import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '@/components/Seo';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
    Shield,
    Lock,
    KeyRound,
    ServerCog,
    CreditCard,
    FileCheck,
    Fingerprint,
    Timer,
    Database,
    Eye,
    Mail,
    ArrowRight,
} from 'lucide-react';

/**
 * Public /security page.
 *
 * Phase 0az (July 2026) — trust-posture landing page. Goal: give coaches
 * and their families a plain-English picture of how SplashPad protects
 * their data, so signup conversion isn't blocked by "is this a random
 * fly-by-night startup with my kid's information?" hesitation.
 *
 * We intentionally do NOT drop into deep infosec jargon; the target
 * reader is a swim coach, not a CISO. The full technical detail lives
 * in /app/SECURITY.md and we link to it at the bottom.
 */

const PILLARS = [
    {
        icon: Lock,
        title: 'Your session token is unreadable to JavaScript',
        body: 'When you log in, we set an httpOnly cookie the browser handles automatically. It cannot be read by any script running on the page, so even a bug elsewhere in the app cannot steal your identity.',
    },
    {
        icon: Fingerprint,
        title: 'Two-factor authentication is built in',
        body: 'Every coach can turn on TOTP (Google Authenticator, Authy, 1Password) or email codes with one tap. We proactively remind coaches to enable it because their account holds their families’ information.',
    },
    {
        icon: KeyRound,
        title: 'Breached passwords are rejected on the spot',
        body: 'When you pick a password we check it against HaveIBeenPwned’s database of known-leaked passwords using a privacy-preserving API. Your password never leaves our server — only a 5-character SHA-1 prefix does.',
    },
    {
        icon: Timer,
        title: 'Brute-force attacks are stopped cold',
        body: 'The login endpoint is rate-limited (5 attempts per minute per IP) and every account locks itself for 15 minutes after 10 failed attempts, even if the attacker rotates IP addresses.',
    },
    {
        icon: CreditCard,
        title: 'We never touch credit card data',
        body: 'Card capture happens inside Stripe’s hosted UI. The card number goes directly from the parent’s browser to Stripe over TLS. SplashPad never sees, transmits, or stores it.',
    },
    {
        icon: Shield,
        title: 'Every coach’s data is walled off',
        body: 'A coach can only ever see their own families and their own payments. Every request is authorized server-side before we return data — client-side checks are for UX only, never for security.',
    },
    {
        icon: FileCheck,
        title: 'Every money move is audited',
        body: 'Charges, refunds, credit grants, and admin password resets all write an immutable audit-log row. If there’s ever a dispute we can reconstruct exactly who did what and when.',
    },
    {
        icon: ServerCog,
        title: 'Locked-down HTTP headers',
        body: 'HSTS, X-Frame-Options: DENY, X-Content-Type-Options: nosniff, a Content-Security-Policy that forbids inline scripts, plus SameSite=Lax cookies to block cross-site request forgery.',
    },
    {
        icon: Database,
        title: 'Deleting a family preserves paid records',
        body: 'When a coach removes a family from their roster, we scrub the coach-scoped data but preserve paid invoices and payments in anonymized form so tax records remain accurate.',
    },
    {
        icon: Eye,
        title: 'No PII in our logs',
        body: 'Passwords never appear in log lines. Emails only show up in audit trails for actions that intrinsically require them (like a refund). Error tracking scrubs identifying info before it leaves the server.',
    },
];

export default function Security() {
    return (
        <div className="min-h-screen bg-[#f4f7fb]" data-testid="security-page">
            <Seo
                title="Security & Trust — SplashPad"
                description="How SplashPad protects your family’s data — encrypted sessions, two-factor authentication, breach-checked passwords, PCI-safe payments, and full audit trails."
                canonical="https://splashpad.pro/security"
            />

            {/* Header — matches the same simple public-page chrome used on /privacy and /terms */}
            <header className="border-b border-[#1a3a5c]/10 bg-white">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
                    <Link to="/" className="flex items-center gap-2" data-testid="security-home-link">
                        <Logo />
                    </Link>
                    <div className="flex items-center gap-2">
                        <Link to="/blog"><Button variant="ghost" size="sm">Blog</Button></Link>
                        <Link to="/login"><Button variant="ghost" size="sm">Log in</Button></Link>
                        <Link to="/signup"><Button size="sm" className="sd-cta sd-focus">Sign up</Button></Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="border-b border-[#1a3a5c]/10 bg-white">
                <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-24">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#00d4ff]">
                        <Shield className="h-3.5 w-3.5" /> Security &amp; trust
                    </div>
                    <h1 className="mt-3 font-display text-4xl font-bold leading-tight text-[#0a1628] sm:text-5xl">
                        Your family&apos;s information deserves a real security team behind it.
                    </h1>
                    <p className="mt-4 text-lg leading-relaxed text-[#1a3a5c]/85">
                        You&apos;re trusting SplashPad with names, emails, waivers for minors, and payment activity. We take that seriously. Below is a plain-English breakdown of exactly how we protect your data — no marketing fluff, no security-theater badges. If you want the full technical version, we publish it too.
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <a href="mailto:security@splashpad.pro">
                            <Button className="sd-cta sd-focus" data-testid="security-report-issue">
                                <Mail className="mr-2 h-4 w-4" /> Report a vulnerability
                            </Button>
                        </a>
                        <a href="#pillars">
                            <Button variant="outline" data-testid="security-see-controls">
                                See the controls <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* Ten security pillars — the meat of the page */}
            <section id="pillars" className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                <div className="mb-8 max-w-3xl">
                    <h2 className="font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">
                        Ten controls, one goal: keep your data yours.
                    </h2>
                    <p className="mt-2 text-sm text-[#1a3a5c]/80">
                        These are the ten controls that most matter for a platform like ours — accounts, payments, and children’s information. Every one of them is live in production today.
                    </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {PILLARS.map(({ icon: Icon, title, body }) => (
                        <Card
                            key={title}
                            className="sd-card p-5"
                            data-testid={`security-pillar-${title.slice(0, 30).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        >
                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#00d4ff]/12 text-[#00d4ff]">
                                <Icon className="h-4 w-4" aria-hidden="true" />
                            </div>
                            <div className="mt-3 font-display text-base font-semibold text-[#0a1628]">
                                {title}
                            </div>
                            <p className="mt-1 text-sm leading-relaxed text-[#1a3a5c]/85">
                                {body}
                            </p>
                        </Card>
                    ))}
                </div>
            </section>

            {/* Payments callout — customers often ask specifically about this */}
            <section className="border-y border-[#1a3a5c]/10 bg-white">
                <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#00d4ff]">
                        <CreditCard className="h-3.5 w-3.5" /> Payments
                    </div>
                    <h2 className="mt-2 font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">
                        Payment security: we’re not the ones storing your card.
                    </h2>
                    <div className="mt-4 grid gap-6 text-sm leading-relaxed text-[#1a3a5c]/85 sm:grid-cols-2">
                        <div>
                            <div className="font-semibold text-[#0a1628]">PCI stance</div>
                            <p className="mt-1">Card numbers, CVVs, and expiration dates flow directly from the parent’s browser to Stripe using their hosted Elements. SplashPad servers never see the card. That means the massive PCI compliance burden lives with Stripe, one of the largest and most-audited payment processors in the world.</p>
                        </div>
                        <div>
                            <div className="font-semibold text-[#0a1628]">Stripe Connect</div>
                            <p className="mt-1">Each coach connects their own Stripe account. Funds flow directly from the parent to the coach; SplashPad never holds customer money. Coaches can refund payments and issue credits with full audit trails, and Stripe verifies every card charge in real time.</p>
                        </div>
                        <div>
                            <div className="font-semibold text-[#0a1628]">Refund + credit ledger</div>
                            <p className="mt-1">Credits are stored as an immutable append-only ledger. Voiding or refunding a credit inserts a compensating entry rather than mutating history — so we can always reconstruct exactly what happened.</p>
                        </div>
                        <div>
                            <div className="font-semibold text-[#0a1628]">Webhook signatures</div>
                            <p className="mt-1">Every incoming Stripe webhook is cryptographically verified against the account-specific signing secret before we act on it. A forged webhook cannot cause charges, refunds, or account changes.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Responsible disclosure */}
            <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#00d4ff]">
                    <Mail className="h-3.5 w-3.5" /> Reporting
                </div>
                <h2 className="mt-2 font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">
                    Found something? We want to hear about it.
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-[#1a3a5c]/85">
                    If you’ve found a vulnerability in SplashPad, please email <a href="mailto:security@splashpad.pro" className="font-semibold text-[#00d4ff] hover:underline">security@splashpad.pro</a>. We commit to a response within one business day and coordinated disclosure. If you give us reasonable time to fix the issue before publishing, we’ll credit you in our release notes (with your permission).
                </p>
                <p className="mt-2 text-xs text-[#1a3a5c]/70">
                    Please do not run automated scanners against production without contacting us first, and never test against real parent or coach accounts other than your own.
                </p>
            </section>

            {/* Footer callout */}
            <section className="border-t border-[#1a3a5c]/10 bg-[#0a1628] text-white">
                <div className="mx-auto max-w-4xl px-4 py-12 text-center sm:px-6">
                    <h2 className="font-display text-2xl font-bold sm:text-3xl">
                        Ready to run your swim school on a platform that takes security this seriously?
                    </h2>
                    <div className="mt-6 flex flex-wrap justify-center gap-3">
                        <Link to="/signup">
                            <Button className="sd-cta sd-focus" data-testid="security-cta-signup">
                                Start your free trial <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </Link>
                        <Link to="/privacy">
                            <Button variant="outline" className="border-white/40 text-white hover:bg-white/10 hover:text-white">
                                Read our privacy policy
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
